import type { Guid } from '../api/public/utils/Guid';
import type { AssetLibraryApiClient } from './asset-library-api-client';

/**
 * One endpoint's params with the tenant taken off.
 *
 * Derived rather than named because the request modules keep their `Params` type private -- if they
 * ever export it, each forwarder below can name it directly and this goes away.
 */
type Scoped<K extends keyof AssetLibraryApiClient> = Omit<Parameters<AssetLibraryApiClient[K]>[0], 'companyId'>;

const _createScopedClient = (client: AssetLibraryApiClient, companyId: Guid) =>
  ({
    /**
     * The company every call below is bound to.
     *
     * Exposed, not hidden, because a consumer that caches per tenant has to name the tenant in its
     * cache key, and reading it off the client it already holds is the one way that cannot
     * disagree with the calls being made. Handing it separately — a `companyId` prop beside a
     * `client` — would let the two drift, and a cache keyed to one tenant while fetching another
     * is a data leak rather than a bug.
     */
    companyId,
    // Folders
    getAssetDirectoryTree: (signal?: AbortSignal) => client.getAssetDirectoryTree({ companyId }, signal),
    createAssetFolder: (params: Scoped<'createAssetFolder'>, signal?: AbortSignal) =>
      client.createAssetFolder({ ...params, companyId }, signal),
    renameAssetFolder: (params: Scoped<'renameAssetFolder'>, signal?: AbortSignal) =>
      client.renameAssetFolder({ ...params, companyId }, signal),
    moveAssetFolder: (params: Scoped<'moveAssetFolder'>, signal?: AbortSignal) =>
      client.moveAssetFolder({ ...params, companyId }, signal),
    archiveAssetDirectory: (params: Scoped<'archiveAssetDirectory'>, signal?: AbortSignal) =>
      client.archiveAssetDirectory({ ...params, companyId }, signal),

    // Assets
    queryAssets: (params: Scoped<'queryAssets'>, signal?: AbortSignal) =>
      client.queryAssets({ ...params, companyId }, signal),
    getAssetDetail: (params: Scoped<'getAssetDetail'>, signal?: AbortSignal) =>
      client.getAssetDetail({ ...params, companyId }, signal),
    uploadAsset: (params: Scoped<'uploadAsset'>, signal?: AbortSignal) =>
      client.uploadAsset({ ...params, companyId }, signal),
    replaceAssetFile: (params: Scoped<'replaceAssetFile'>, signal?: AbortSignal) =>
      client.replaceAssetFile({ ...params, companyId }, signal),
    renameAsset: (params: Scoped<'renameAsset'>, signal?: AbortSignal) =>
      client.renameAsset({ ...params, companyId }, signal),
    copyAssets: (params: Scoped<'copyAssets'>, signal?: AbortSignal) =>
      client.copyAssets({ ...params, companyId }, signal),
    moveAssets: (params: Scoped<'moveAssets'>, signal?: AbortSignal) =>
      client.moveAssets({ ...params, companyId }, signal),
    archiveAsset: (params: Scoped<'archiveAsset'>, signal?: AbortSignal) =>
      client.archiveAsset({ ...params, companyId }, signal),
    archiveAssets: (params: Scoped<'archiveAssets'>, signal?: AbortSignal) =>
      client.archiveAssets({ ...params, companyId }, signal),
  }) as const;

/**
 * The same 14 endpoints as `AssetLibraryApiClient`, with the company already applied.
 *
 * Every route takes the company in its path and it cannot change while a library is on screen, so
 * binding it once here is what lets a consumer stop threading a tenant through every call. The
 * company is readable as `companyId` for the one thing binding cannot do for a consumer: name the
 * tenant in a cache key. A new endpoint on the unscoped client is not automatically here -- it has
 * to be forwarded above.
 */
export type CompanyScopedAssetLibraryClient = ReturnType<typeof _createScopedClient>;

export const createCompanyScopedAssetLibraryClient = (
  client: AssetLibraryApiClient,
  companyId: Guid
): CompanyScopedAssetLibraryClient => _createScopedClient(client, companyId);
